import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet, apiPost } from "../../app/api";
import { dateDaysFromNow, formatQuantity, locationLabel } from "../../display";
import type { Location } from "../../types";

type CountCatalogRow = {
  row_type?: "item" | "section" | string;
  label?: string;
  product_id?: number;
  sku?: string;
  name?: string;
  uom?: string;
  system_qty?: number;
  matched?: boolean;
  highlighted?: boolean;
  required_quantity?: boolean;
};

type CountCatalogPayload = {
  location?: Location | null;
  rows?: CountCatalogRow[];
  template?: string;
};

type CountLineInput = {
  product_id: number;
  counted_qty: number;
  reason?: string;
};

type CountPostResult = {
  ok: boolean;
  stock_count_id?: number;
};

type CountEntry = {
  quantity: string;
  reason: string;
};

function rowKey(row: CountCatalogRow, index: number): string {
  return `${row.row_type || "item"}-${row.product_id || row.label || "row"}-${index}`;
}

function itemName(row: CountCatalogRow): string {
  return row.name || row.sku || row.label || "Inventory item";
}

function cleanQuantity(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

export function StoreCountWorkspace({ csrfToken }: { csrfToken: string }) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState("");
  const [countDate, setCountDate] = useState(dateDaysFromNow(0));
  const [catalogRows, setCatalogRows] = useState<CountCatalogRow[]>([]);
  const [template, setTemplate] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [requireReasons, setRequireReasons] = useState(false);
  const [entries, setEntries] = useState<Record<number, CountEntry>>({});
  const [message, setMessage] = useState<{ variant: "success" | "danger" | "warning"; title: string } | null>(null);

  const matchedRows = useMemo(
    () => catalogRows.filter(row => row.row_type !== "section" && row.product_id && row.matched !== false),
    [catalogRows]
  );
  const missingRows = useMemo(
    () => catalogRows.filter(row => row.row_type !== "section" && (!row.product_id || row.matched === false)),
    [catalogRows]
  );
  const filledCount = useMemo(() => {
    return matchedRows.filter(row => {
      const productId = Number(row.product_id || 0);
      return productId > 0 && cleanQuantity(entries[productId]?.quantity || "") !== null;
    }).length;
  }, [entries, matchedRows]);

  const loadCatalog = useCallback(async (nextLocationId: string) => {
    if (!nextLocationId) {
      setCatalogRows([]);
      setTemplate("");
      setLoading(false);
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<CountCatalogPayload>(`/api/stock-count-catalog?location_id=${encodeURIComponent(nextLocationId)}`);
      setCatalogRows(payload.rows || []);
      setTemplate(payload.template || "");
      if (payload.location?.id) setLocationId(String(payload.location.id));
      setEntries({});
    } catch (err) {
      setCatalogRows([]);
      setTemplate("");
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Inventory count checklist could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLocationsAndCatalog = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<CountCatalogPayload>("/api/stock-count-catalog");
      const firstLocation = payload.location;
      setLocations(firstLocation ? [firstLocation] : []);
      setLocationId(firstLocation?.id ? String(firstLocation.id) : "");
      setCatalogRows(payload.rows || []);
      setTemplate(payload.template || "");
      setEntries({});
    } catch (err) {
      setLocations([]);
      setCatalogRows([]);
      setTemplate("");
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Inventory count checklist could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVisibleLocations = useCallback(async () => {
    try {
      const payload = await apiGet<{ locations: Location[] }>("/api/locations");
      setLocations(payload.locations || []);
    } catch {
      setLocations(current => current);
    }
  }, []);

  useEffect(() => {
    void loadLocationsAndCatalog();
    void loadVisibleLocations();
  }, [loadLocationsAndCatalog, loadVisibleLocations]);

  const selectedLocation = useMemo(
    () => locations.find(location => String(location.id) === String(locationId)),
    [locationId, locations]
  );

  const updateEntry = (productId: number, patch: Partial<CountEntry>) => {
    setEntries(current => ({
      ...current,
      [productId]: {
        quantity: current[productId]?.quantity || "",
        reason: current[productId]?.reason || "",
        ...patch
      }
    }));
  };

  const submitCount = async () => {
    const lines: CountLineInput[] = [];
    for (const row of matchedRows) {
      const productId = Number(row.product_id || 0);
      const quantity = cleanQuantity(entries[productId]?.quantity || "");
      if (!productId || quantity === null) continue;
      lines.push({
        product_id: productId,
        counted_qty: quantity,
        reason: entries[productId]?.reason?.trim() || undefined
      });
    }
    if (!lines.length) {
      setMessage({ variant: "warning", title: "Enter at least one counted quantity before posting the count." });
      return;
    }
    setPosting(true);
    setMessage(null);
    try {
      await apiPost<CountPostResult>("/api/stock-counts", {
        location_id: locationId,
        count_date: countDate,
        require_variance_reasons: requireReasons,
        lines
      }, csrfToken);
      setEntries({});
      setMessage({ variant: "success", title: "Inventory count posted and stock balances updated." });
      await loadCatalog(locationId);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Inventory count could not be posted." });
    } finally {
      setPosting(false);
    }
  };

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-store-count-view" className="pf-toj-store-count">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>Enter Inventory Count</h1>
            <p>Choose the location, enter counted quantities, and post the count when the sheet is finished.</p>
          </FlexItem>
          {loading && (
            <FlexItem>
              <Spinner size="md" aria-label="Loading inventory count" />
            </FlexItem>
          )}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <Card className="pf-toj-work-card pf-toj-primary-work-card">
          <CardBody>
            <Form className="pf-toj-count-controls">
              <FormGroup label="Location" fieldId="pf-count-location">
                <FormSelect
                  id="pf-count-location"
                  aria-label="Count location"
                  value={locationId}
                  onChange={(_event, value) => {
                    const nextValue = String(value);
                    setLocationId(nextValue);
                    void loadCatalog(nextValue);
                  }}
                >
                  {!locations.length && <FormSelectOption value="" label="No count locations available" />}
                  {locations.map(location => (
                    <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />
                  ))}
                </FormSelect>
              </FormGroup>
              <FormGroup label="Count date" fieldId="pf-count-date">
                <TextInput
                  id="pf-count-date"
                  type="date"
                  value={countDate}
                  onChange={(_event, value) => setCountDate(String(value))}
                  aria-label="Count date"
                />
              </FormGroup>
              <FormGroup label="Variance notes" fieldId="pf-count-require-reasons">
                <Checkbox
                  id="pf-count-require-reasons"
                  label="Require a note when counted quantity is different"
                  isChecked={requireReasons}
                  onChange={(_event, checked) => setRequireReasons(Boolean(checked))}
                />
              </FormGroup>
            </Form>
            <div className="pf-toj-inventory-summary" aria-label="Inventory count summary">
              <span><strong>{matchedRows.length}</strong> countable items</span>
              <span><strong>{filledCount}</strong> quantities entered</span>
              <span><strong>{missingRows.length}</strong> setup gaps</span>
              {template && <span><strong>{template === "warehouse_physical_count" ? "Warehouse" : "Nightly"}</strong> checklist</span>}
            </div>
            {selectedLocation && (
              <p className="pf-toj-muted pf-toj-count-note">
                {locationLabel(selectedLocation)} is the active count location. Blank quantity rows are ignored.
              </p>
            )}
            <div className="pf-toj-table-wrap pf-toj-count-table-wrap">
              <Table aria-label="Inventory count checklist" variant="compact">
                <Thead>
                  <Tr>
                    <Th>Count item</Th>
                    <Th>Matched item</Th>
                    <Th>Unit</Th>
                    <Th textCenter>System qty</Th>
                    <Th>Counted qty</Th>
                    <Th>Note</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {catalogRows.map((row, index) => {
                    if (row.row_type === "section") {
                      return (
                        <Tr key={rowKey(row, index)} className="pf-toj-count-section-row">
                          <Td colSpan={6}>{row.label || "Section"}</Td>
                        </Tr>
                      );
                    }
                    const productId = Number(row.product_id || 0);
                    const matched = productId > 0 && row.matched !== false;
                    return (
                      <Tr key={rowKey(row, index)} className={row.highlighted ? "pf-toj-count-highlight-row" : ""}>
                        <Td dataLabel="Count item">
                          <strong>{row.label || itemName(row)}</strong>
                          {row.required_quantity && <Badge isRead>Required</Badge>}
                        </Td>
                        <Td dataLabel="Matched item">
                          {matched ? (
                            <>
                              <strong>{itemName(row)}</strong>
                              <small>{row.sku || "No SKU"}</small>
                            </>
                          ) : (
                            <span className="pf-toj-count-missing">Needs item setup</span>
                          )}
                        </Td>
                        <Td dataLabel="Unit">{matched ? row.uom || "Not set" : ""}</Td>
                        <Td dataLabel="System qty" textCenter>{matched ? formatQuantity(row.system_qty) : ""}</Td>
                        <Td dataLabel="Counted qty">
                          <TextInput
                            type="number"
                            min={0}
                            step="0.01"
                            value={matched ? entries[productId]?.quantity || "" : ""}
                            isDisabled={!matched}
                            placeholder={matched ? "Qty" : "Missing item"}
                            aria-label={`Counted quantity for ${row.label || itemName(row)}`}
                            onChange={(_event, value) => updateEntry(productId, { quantity: String(value) })}
                          />
                        </Td>
                        <Td dataLabel="Note">
                          <TextInput
                            value={matched ? entries[productId]?.reason || "" : ""}
                            isDisabled={!matched}
                            placeholder="Optional"
                            aria-label={`Count note for ${row.label || itemName(row)}`}
                            onChange={(_event, value) => updateEntry(productId, { reason: String(value) })}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                  {!catalogRows.length && !loading && (
                    <Tr>
                      <Td colSpan={6}>
                        <div className="pf-toj-empty">
                          <strong>No count checklist is available.</strong>
                          <span>Choose another location or review item visibility setup.</span>
                        </div>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </div>
            <div className="pf-toj-sticky-action-bar">
              <span>{filledCount} quantity {filledCount === 1 ? "line" : "lines"} ready</span>
              <Button variant="primary" onClick={() => void submitCount()} isLoading={posting} isDisabled={posting || !filledCount}>
                Post inventory count
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </PageSection>
  );
}
